import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, Input, Button, Space, Select } from "antd";
import { UserOutlined, MailOutlined, PhoneOutlined, GlobalOutlined } from "@ant-design/icons";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";
import { useAuth } from "../../context/AuthContext";

const { Option } = Select;

const CreateGuestModal = ({
  visible,
  onCancel,
  onSubmit,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const { userProfile } = useAuth();
  const [airlines, setAirlines] = useState([]);
  const [airlinesLoading, setAirlinesLoading] = useState(false);

  const authRole = localStorage.getItem("authRole");
  const isAirline = authRole === "airline" || userProfile?.userType === "Airline";
  const airlineId = isAirline ? (userProfile?.id || userProfile?._id) : undefined;

  const fetchAirlines = useCallback(() => {
    setAirlinesLoading(true);
    request({
      url: apiPath.getAirlines,
      method: "GET",
      onSuccess: (res) => {
        if (res.success) {
          setAirlines(res.airlines || []);
        }
      },
      onError: (err) => {
        console.error("Error fetching airlines:", err);
      },
      finally: () => setAirlinesLoading(false),
    });
  }, [request]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (isAirline && airlineId) {
        form.setFieldsValue({ airline_id: airlineId });
      }
      if (!isAirline) {
        fetchAirlines();
      }
    }
  }, [visible, form, isAirline, airlineId, fetchAirlines]);

  const onFinish = (values) => {
    // Ensure airline_id is included for airline users even if field is hidden
    const submissionValues = {
      ...values,
      airline_id: isAirline ? airlineId : values.airline_id
    };
    onSubmit(submissionValues);
  };

  return (
    <Modal
      title="Create New Guest"
      open={visible}
      onCancel={onCancel}
      footer={null}
      centered
      width={480}
      destroyOnClose
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          full_name: "",
          email: "",
          phone_number: "",
          airline_id: isAirline ? airlineId : undefined,
        }}
      >
        {!isAirline && (
          <Form.Item
            name="airline_id"
            label="Select Airline"
            rules={[{ required: true, message: "Please select an airline" }]}
          >
            <Select
              placeholder="Select an airline"
              loading={airlinesLoading}
              size="large"
              suffixIcon={<GlobalOutlined style={{ color: "#bfbfbf" }} />}
            >
              {airlines.map((airline) => (
                <Option key={airline._id} value={airline._id}>
                  {airline.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
  name="full_name"
  label="Full Name"
  rules={[
    {
      required: true,
      whitespace: true,
      message: "Please enter guest's full name",
    },
  ]}
>
  <Input
    prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
    placeholder="John Doe"
    size="large"
  />
</Form.Item>

<Form.Item
  name="email"
  label="Email"
  rules={[
    {
      required: true,
      whitespace: true,
      message: "Please enter guest's email",
    }
  ]}
>
  <Input
    prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
    placeholder="john.doe@example.com"
    size="large"
  />
</Form.Item>

<Form.Item
  name="phone_number"
  label="Phone Number"
  rules={[
    {
      required: true,
      whitespace: true,
      message: "Please enter guest's phone number",
    },
  ]}
>
  <Input
    prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
    placeholder="+1 234 567 890"
    size="large"
  />
</Form.Item>

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{
                borderRadius: 8,
                fontWeight: 600,
              }}
            >
              Create Guest
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateGuestModal;
